import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import ContentWrapper from '../../components/ContentWrapper';
import TabNavigation from '../../components/TabNavigation';
import { useSettings } from '../../context/settingsContext';
import { getCurrency } from '../../utils/getCurrency';
import KpiPanel from './components/KpiPanel';
import PartnerMultiSelect from './components/PartnerMultiSelect';
import ReportCard, { ReportDescriptor } from './components/ReportCard';
import { readEnum, readPartners, withParam, writePartners } from './urlState';

type Tab = 'reports' | 'kpis';
const tabs: Tab[] = ['reports', 'kpis'];

const ReportDescription = styled.div`
  margin: 0.125rem 0;
  p {
    margin: 0 0 0.0625rem 0;
  }

  span {
    font-size: 0.9rem;
    font-weight: 600;
  }
`;

// The CSV reports on the Reports tab, each combined across the selected
// partners (shared header once). The first column names the partner: the
// summary reports carry it themselves as partnerName; the others get a
// partner_name column prepended. Every row ends with a currency column: the
// partner's, except bank transactions, where it is the transaction's.
const REPORTS: ReportDescriptor[] = [
  {
    key: 'transaction',
    title: 'Transaction report',
    description: (
      <ReportDescription>
        <p>
          Index: <span>partner_name, eventKey</span>
        </p>
        Events that changed a claim&apos;s balance (payout, reserve, recovery
        or recovery reserve) in the selected period. One row per
        balance-changing event per claim per partner. Amounts are in the
        row&apos;s currency column.
      </ReportDescription>
    ),
    hasDates: true,
    fileName: 'transaction_report',
  },
  {
    key: 'fullTransaction',
    title: 'Full transaction report',
    description: (
      <ReportDescription>
        <p>
          Index: <span>partner_name, eventKey</span>
        </p>
        The transaction report plus claim case level events that change no
        balance (e.g. claim case created or closed). One row per claim case or
        claim level event per partner in the selected period. Amounts are in
        the row&apos;s currency column.
      </ReportDescription>
    ),
    hasDates: true,
    fileName: 'full_transaction_report',
  },
  {
    key: 'summary',
    title: 'Summary report',
    description: (
      <ReportDescription>
        <p>
          Index: <span>partnerName, claimcaseKey</span>
        </p>
        Summary on claim case level, including policy information. One row per
        claim case per partner. Amounts are in the row&apos;s currency column.
      </ReportDescription>
    ),
    fileName: 'summary_report',
  },
  {
    key: 'claimSummary',
    title: 'Claim summary report',
    description: (
      <ReportDescription>
        <p>
          Index: <span>partnerName, claimKey</span>
        </p>
        Summary on claim level. One row per claim per partner (a claim case
        can contain several claims). Amounts are in the row&apos;s currency
        column.
      </ReportDescription>
    ),
    fileName: 'claim_summary_report',
  },
  {
    key: 'bankTransaction',
    title: 'Bank transaction report',
    description: (
      <ReportDescription>
        All transactions on each partner&apos;s SEB bank account, matched to
        claim payments where possible. One row per bank account transaction
        per partner; the partner_name column identifies the partner. The
        amount is in the row&apos;s currency column: the currency of the
        individual bank transaction.
      </ReportDescription>
    ),
    fileName: 'bank_transaction_report',
  },
  {
    key: 'scheduledPayments',
    title: 'Scheduled payments report',
    description: (
      <ReportDescription>
        <p>
          Index: <span>partner_name, paymentKey</span>
        </p>
        Payments that are signed but not yet paid out, for open claims. One
        row per scheduled payment per partner. Amounts are in the row&apos;s
        currency column.
      </ReportDescription>
    ),
    fileName: 'scheduled_payments_report',
  },
];

const ReportsView = () => {
  // The tab and the partner selection are URL parameters (see urlState.ts),
  // so Back returns to the previous view.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: Tab = readEnum(searchParams.get('tab'), tabs) ?? 'reports';
  const setTab = (next: Tab) =>
    setSearchParams(withParam(searchParams, 'tab', next));
  // null = every partner the user has access to (the default), so a partner
  // list that finishes loading later is still "all selected".
  const selectedPartners = readPartners(searchParams);
  const setSelectedPartners = (selected: string[] | null) =>
    setSearchParams(writePartners(searchParams, selected));
  const { partners } = useSettings();

  localStorage.setItem(
    'activeView',
    JSON.stringify({ view: 'reports', url: null })
  );

  const partnerList = partners ?? [];
  const partnerNames = partnerList.map((partner) => partner.name);
  const effectiveSelection = selectedPartners ?? partnerNames;
  const allSelected = effectiveSelection.length === partnerNames.length;
  // Omit the partner params entirely when everything is selected, so the
  // backend applies its own "all accessible partners" default. Memoized on the
  // joined names: KpiPanel refetches when this changes, and a fresh array
  // every render is a new identity every render.
  const partnersKey = effectiveSelection.join(',');
  const partnersParam = useMemo(
    () => (allSelected ? undefined : effectiveSelection),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allSelected, partnersKey]
  );
  const noPartnersSelected =
    partnerNames.length > 0 && effectiveSelection.length === 0;
  // Only the CSV reports carry amounts, so the mixed-currency hint is shown
  // with the report cards and not on the KPIs tab.
  const selectedCurrencies = Array.from(
    new Set(
      partnerList
        .filter((partner) => effectiveSelection.includes(partner.name))
        .map((partner) => getCurrency(partner.locale))
    )
  ).sort();

  return (
    <ContentWrapper>
      <header>
        <h1>Reports</h1>
      </header>
      <PartnerMultiSelect
        partners={partnerList}
        selected={selectedPartners}
        onChange={setSelectedPartners}
      />
      {noPartnersSelected && <Hint>Select at least one partner.</Hint>}
      <TabNavigation
        size='medium'
        selectedIndex={tabs.indexOf(tab)}
        onClick={(index) => setTab(tabs[index])}
        items={[
          { content: <span>Reports</span> },
          { content: <span>KPIs</span> },
        ]}
      />
      {tab === 'reports' ? (
        <ReportStack>
          {selectedCurrencies.length > 1 && (
            <Hint>
              Selected partners report in {selectedCurrencies.join(', ')}.
              Each row&apos;s currency column says which; amounts are not
              comparable across currencies.
            </Hint>
          )}
          {REPORTS.map((report) => (
            <ReportCard
              key={report.key}
              report={report}
              partnersParam={partnersParam}
              disabled={noPartnersSelected}
            />
          ))}
        </ReportStack>
      ) : (
        <KpiSection>
          <KpiPanel
            partnersParam={partnersParam}
            selectedPartners={effectiveSelection}
            disabled={noPartnersSelected}
          />
        </KpiSection>
      )}
    </ContentWrapper>
  );
};

export default ReportsView;

const ReportStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 46rem;
`;

const KpiSection = styled.section`
  max-width: 60rem;
`;

const Hint = styled.p`
  color: #7b7d8c;
  font-size: 0.85rem;
  margin: 0;
`;
